import React, { useState } from "react";
import { toast } from "sonner";
import { Upload, FileText, Check, X, ArrowLeft, ExternalLink, AlertTriangle, Search, Layers } from "lucide-react";
import { StaffShell } from "@/components/shells";
import { Panel, Spinner, Btn, Tag, Field, Input, Textarea, EmptyState } from "@/components/kit";
import { useAsync } from "@/lib/hooks";
import { api } from "@/lib/api";
import { supabase } from "@/lib/supabase";
import { fmtDateTime } from "@/lib/vda";

const STATUS_TONE = {
  matched: "green", applied: "muted", review_required: "amber",
  unmatched: "amber", rejected: "red", duplicate: "muted", failed: "red",
};
const STATUS_LABEL = {
  matched: "Matched", applied: "Applied", review_required: "Review required",
  unmatched: "Unmatched", rejected: "Rejected", duplicate: "Duplicate", failed: "Failed",
};

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result).split(",")[1] || "");
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}

export default function Confirmations() {
  const [selected, setSelected] = useState(null);
  const [statusFilter, setStatusFilter] = useState("");
  const { data, loading, reload } = useAsync(() => api.confList(statusFilter), [statusFilter]);

  if (selected) {
    return <ReviewScreen cid={selected} onBack={() => { setSelected(null); reload(); }} />;
  }

  return (
    <StaffShell>
      <p className="vda-tick text-[hsl(var(--vda-quiet))]">Service / MOT</p>
      <h1 className="text-[26px] font-semibold tracking-tight text-foreground mb-1">Booking confirmations</h1>
      <p className="text-sm text-muted-foreground mb-6 max-w-2xl">
        Intake garage confirmation emails from Venson, match them to the right VDA request, review the extracted detail, then apply to confirm the booking for the driver.
      </p>

      <IntakePanel onDone={reload} onReview={(cid) => setSelected(cid)} />

      <div className="mt-8 flex items-center justify-between gap-3 flex-wrap">
        <h2 className="font-semibold">Received confirmations</h2>
        <div className="flex gap-2 flex-wrap" data-testid="conf-filters">
          {[["", "All"], ["review_required", "Review required"], ["matched", "Matched"], ["unmatched", "Unmatched"], ["applied", "Applied"], ["rejected", "Rejected"]].map(([v, l]) => (
            <button key={v || "all"} data-testid={`conf-filter-${v || "all"}`} onClick={() => setStatusFilter(v)}
              className={`h-8 px-3 text-xs rounded-[6px] border transition-colors ${statusFilter === v ? "bg-primary text-primary-foreground border-primary" : "border-[hsl(var(--input))] bg-card hover:bg-secondary"}`}>{l}</button>
          ))}
        </div>
      </div>

      {loading ? <Spinner /> : (data || []).length === 0 ? (
        <div className="mt-4"><EmptyState title="No confirmations yet" hint="Upload a .msg file or paste an email above to get started." /></div>
      ) : (
        <Panel className="mt-4 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[820px]">
              <thead>
                <tr className="bg-secondary/60 text-left vda-tick text-muted-foreground">
                  <th className="px-3 py-2 font-medium">Registration</th>
                  <th className="px-3 py-2 font-medium">VDA ref</th>
                  <th className="px-3 py-2 font-medium">Garage</th>
                  <th className="px-3 py-2 font-medium">Appointment</th>
                  <th className="px-3 py-2 font-medium">Source</th>
                  <th className="px-3 py-2 font-medium">Status</th>
                  <th className="px-3 py-2 font-medium">Received</th>
                </tr>
              </thead>
              <tbody>
                {(data || []).map((c) => (
                  <tr key={c.id} onClick={() => setSelected(c.id)} data-testid={`conf-row-${c.id}`}
                    className="border-t border-border cursor-pointer hover:bg-secondary/50">
                    <td className="px-3 py-2.5 vda-reg">{c.extracted_registration || "—"}</td>
                    <td className="px-3 py-2.5 vda-reg text-muted-foreground">{c.extracted_vda_reference || "—"}</td>
                    <td className="px-3 py-2.5">{c.extracted_garage_name || "—"}</td>
                    <td className="px-3 py-2.5">{c.extracted_appointment_at ? fmtDateTime(c.extracted_appointment_at) : "—"}</td>
                    <td className="px-3 py-2.5 text-xs text-muted-foreground">{c.source_type?.replace(/_/g, " ")}</td>
                    <td className="px-3 py-2.5"><Tag tone={STATUS_TONE[c.review_status] || "muted"}>{STATUS_LABEL[c.review_status] || c.review_status}</Tag></td>
                    <td className="px-3 py-2.5 font-mono text-[11px] text-[hsl(var(--vda-quiet))]">{fmtDateTime(c.received_at || c.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Panel>
      )}
    </StaffShell>
  );
}

function IntakePanel({ onDone, onReview }) {
  const [mode, setMode] = useState("upload");
  const [busy, setBusy] = useState(false);
  const [fileName, setFileName] = useState("");
  const [fileB64, setFileB64] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [sender, setSender] = useState("DoNotReply@venson.com");
  const [bulkFiles, setBulkFiles] = useState([]);
  const [bulkResults, setBulkResults] = useState(null);

  const pickFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFileName(f.name);
    setFileB64(await fileToBase64(f));
  };

  const bulkSubmit = async () => {
    if (!bulkFiles.length) { toast.error("Choose one or more .msg files."); return; }
    setBusy(true);
    try {
      const files = await Promise.all(bulkFiles.map(async (f) => ({ source_file_name: f.name, file_base64: await fileToBase64(f) })));
      const res = await api.confBulkIntake(files);
      setBulkResults(res);
      const c = res.counts || {};
      toast.success(`Processed ${files.length}: ${c.matched || 0} matched · ${c.review_required || 0} to review · ${c.unmatched || 0} unmatched · ${c.duplicate || 0} duplicate · ${c.failed || 0} failed.`);
      onDone?.();
    } catch (e) {
      toast.error(e.message || "Bulk intake failed.");
    } finally { setBusy(false); }
  };

  const submit = async () => {
    if (mode === "bulk") return bulkSubmit();
    setBusy(true);
    try {
      let payload;
      if (mode === "upload") {
        if (!fileB64) { toast.error("Choose a .msg file first."); setBusy(false); return; }
        payload = { source_type: "msg_upload", source_file_name: fileName, file_base64: fileB64 };
      } else {
        if (!body.trim()) { toast.error("Paste the email body first."); setBusy(false); return; }
        payload = { source_type: "pasted_body", subject, body, sender };
      }
      const res = await api.confIntake(payload);
      if (res.status === "duplicate") toast.info("This confirmation was already received.");
      else toast.success(`Confirmation received — ${STATUS_LABEL[res.status] || res.status}.`);
      if (res.warnings?.length) toast.warning(`Parser notes: ${res.warnings.join(", ")}`);
      setFileName(""); setFileB64(""); setSubject(""); setBody("");
      onDone?.();
    } catch (e) {
      toast.error(e.message || "Intake failed.");
    } finally { setBusy(false); }
  };

  const MODE_BTN = (m, Icon, label) => (
    <button data-testid={`intake-mode-${m}`} onClick={() => setMode(m)}
      className={`h-9 px-4 text-sm rounded-[6px] border inline-flex items-center gap-2 transition-colors ${mode === m ? "bg-primary text-primary-foreground border-primary" : "border-[hsl(var(--input))] bg-card hover:bg-secondary"}`}>
      <Icon className="h-4 w-4" /> {label}
    </button>
  );

  return (
    <Panel className="p-5" data-testid="conf-intake">
      <div className="flex gap-2 mb-4 flex-wrap">
        {MODE_BTN("upload", Upload, "Upload .msg")}
        {MODE_BTN("paste", FileText, "Paste email text")}
        {MODE_BTN("bulk", Layers, "Bulk .msg")}
      </div>

      {mode === "upload" ? (
        <div className="space-y-3">
          <Field label="Outlook confirmation (.msg)">
            <label className="flex items-center gap-3 border border-dashed border-[hsl(var(--input))] rounded-[8px] px-4 py-6 cursor-pointer hover:bg-secondary/40" data-testid="msg-dropzone">
              <Upload className="h-5 w-5 text-muted-foreground" />
              <span className="text-sm text-muted-foreground">{fileName || "Click to choose a .msg file"}</span>
              <input type="file" accept=".msg" className="hidden" onChange={pickFile} data-testid="msg-file-input" />
            </label>
          </Field>
        </div>
      ) : mode === "paste" ? (
        <div className="space-y-3">
          <Field label="From (sender)"><Input data-testid="paste-sender" value={sender} onChange={(e) => setSender(e.target.value)} /></Field>
          <Field label="Subject"><Input data-testid="paste-subject" value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Your booking for vehicle reg AB12 CDE has been confirmed" /></Field>
          <Field label="Email body"><Textarea data-testid="paste-body" rows={8} value={body} onChange={(e) => setBody(e.target.value)} placeholder="Paste the full confirmation email text here" className="font-mono text-xs" /></Field>
        </div>
      ) : (
        <div className="space-y-3" data-testid="conf-bulk">
          <Field label="Outlook confirmations (.msg) — select several at once">
            <label className="flex items-center gap-3 border border-dashed border-[hsl(var(--input))] rounded-[8px] px-4 py-6 cursor-pointer hover:bg-secondary/40" data-testid="bulk-dropzone">
              <Layers className="h-5 w-5 text-muted-foreground" />
              <span className="text-sm text-muted-foreground">{bulkFiles.length ? `${bulkFiles.length} file(s) selected` : "Click to choose multiple .msg files"}</span>
              <input type="file" accept=".msg" multiple className="hidden" data-testid="bulk-file-input"
                onChange={(e) => { setBulkFiles(Array.from(e.target.files || [])); setBulkResults(null); }} />
            </label>
          </Field>
          {bulkResults && (
            <div>
              <div className="flex flex-wrap gap-2 mb-2" data-testid="bulk-counts">
                {Object.entries(bulkResults.counts || {}).filter(([, n]) => n > 0).map(([k, n]) => (
                  <Tag key={k} tone={STATUS_TONE[k] || "muted"}>{STATUS_LABEL[k] || k}: {n}</Tag>
                ))}
              </div>
              <div className="border border-border rounded-[8px] divide-y divide-border" data-testid="bulk-results">
                {bulkResults.results.map((r, i) => (
                  <div key={i} className="flex items-center justify-between gap-3 px-3 py-2 text-sm" data-testid={`bulk-result-${i}`}>
                    <div className="min-w-0">
                      <div className="truncate font-mono text-xs">{r.file}</div>
                      {r.error && <div className="text-xs text-[hsl(var(--vda-red))]">{r.error}</div>}
                      {r.warnings?.length > 0 && <div className="text-[11px] text-muted-foreground">{r.warnings.join(", ")}</div>}
                    </div>
                    <div className="flex items-center gap-2 flex-none">
                      <Tag tone={STATUS_TONE[r.status] || "muted"}>{STATUS_LABEL[r.status] || r.status}</Tag>
                      {r.confirmation_id && <Btn variant="outline" data-testid={`bulk-review-${i}`} onClick={() => onReview?.(r.confirmation_id)}>Review</Btn>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      <div className="mt-4 flex items-center justify-between gap-3">
        <p className="text-xs text-muted-foreground">Only emails from <span className="font-mono">venson.com</span> are trusted. Nothing is applied automatically.</p>
        <Btn data-testid="conf-submit" onClick={submit} loading={busy}>{mode === "bulk" ? "Process files" : "Receive confirmation"}</Btn>
      </div>
    </Panel>
  );
}

function ReviewScreen({ cid, onBack }) {
  const { data, loading, reload } = useAsync(() => api.confDetail(cid), [cid]);
  const [busy, setBusy] = useState(false);
  const [rejectReason, setRejectReason] = useState("");

  if (loading) return <StaffShell><Spinner /></StaffShell>;
  const c = data?.confirmation;
  if (!c) return <StaffShell><EmptyState title="Confirmation not found" /></StaffShell>;
  const pb = data.proposed_booking;
  const candidates = data.candidates || [];
  const warnings = (c.parsed_data?.warnings) || [];
  const senderOk = c.parsed_data?.sender_validated;
  const isApplied = c.review_status === "applied";
  const isRejected = c.review_status === "rejected";
  const smsPreview = data.sms_preview;
  const mobileCheck = data.mobile_check || {};
  const mobileMismatch = mobileCheck.result === "mismatch";

  const run = async (fn, ok) => {
    setBusy(true);
    try { await fn(); toast.success(ok); reload(); }
    catch (e) { toast.error(e.message || "Action failed"); }
    finally { setBusy(false); }
  };

  const choose = (bid) => run(() => api.confMatch(cid, bid), "Matched to booking.");
  const apply = () => run(() => api.confApply(cid), "Confirmation applied — booking confirmed.");
  const reject = () => {
    if (!rejectReason.trim()) { toast.error("Add a short reason for rejecting."); return; }
    run(() => api.confReject(cid, rejectReason), "Confirmation rejected.");
  };

  const extracted = [
    ["Registration", c.extracted_registration],
    ["VDA reference", c.extracted_vda_reference],
    ["Booking reference", c.extracted_booking_reference],
    ["Appointment", c.extracted_appointment_at ? fmtDateTime(c.extracted_appointment_at) : null],
    ["Garage", c.extracted_garage_name],
    ["Garage phone", c.extracted_garage_phone],
    ["Garage address", c.extracted_garage_address],
    ["Booking type", c.extracted_booking_type],
    ["Courtesy vehicle", c.extracted_courtesy_vehicle],
    ["Work summary", c.extracted_work_summary],
  ];

  return (
    <StaffShell>
      <button onClick={onBack} data-testid="conf-back" className="text-sm text-muted-foreground mb-4 hover:text-foreground inline-flex items-center gap-1"><ArrowLeft className="h-4 w-4" /> Back to confirmations</button>

      <div className="flex items-start justify-between gap-3 flex-wrap mb-4">
        <div>
          <p className="vda-tick text-[hsl(var(--vda-quiet))]">Review confirmation</p>
          <h1 className="text-[24px] font-semibold tracking-tight text-foreground">{c.extracted_registration || "Unknown vehicle"}</h1>
        </div>
        <Tag tone={STATUS_TONE[c.review_status] || "muted"}>{STATUS_LABEL[c.review_status] || c.review_status}</Tag>
      </div>

      {(!senderOk || warnings.length > 0) && (
        <Panel className="p-3 mb-4 bg-[hsl(var(--vda-amber-bg))] border border-[hsl(var(--vda-amber))]/25" data-testid="conf-warnings">
          <div className="flex items-start gap-2 text-sm text-[hsl(var(--vda-amber))]">
            <AlertTriangle className="h-4 w-4 mt-0.5 flex-none" />
            <div>
              {!senderOk && <div className="font-medium">Sender not verified as venson.com — check before applying.</div>}
              {warnings.length > 0 && <div className="text-xs mt-1 text-muted-foreground">Parser notes: {warnings.join(", ")}</div>}
            </div>
          </div>
        </Panel>
      )}

      <div className="grid lg:grid-cols-2 gap-5">
        {/* Extracted from email */}
        <Panel className="p-5" data-testid="conf-extracted">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-semibold">Extracted from email</h2>
            {data.raw_url && <a href={data.raw_url} target="_blank" rel="noreferrer" className="text-xs text-primary inline-flex items-center gap-1" data-testid="conf-raw-link">Original <ExternalLink className="h-3 w-3" /></a>}
          </div>
          <dl className="space-y-2 text-sm">
            {extracted.map(([l, v]) => (
              <div key={l} className="grid grid-cols-[140px_1fr] gap-3">
                <dt className="vda-tick text-muted-foreground">{l}</dt>
                <dd className={v ? "" : "text-muted-foreground/60"}>{v || "—"}</dd>
              </div>
            ))}
          </dl>
          <div className="mt-3 pt-3 border-t border-border text-xs text-muted-foreground font-mono">
            {c.source_type?.replace(/_/g, " ")} · from {c.original_sender || "unknown"}
          </div>
        </Panel>

        {/* Proposed VDA booking */}
        <Panel className="p-5" data-testid="conf-proposed">
          <h2 className="font-semibold mb-3">Proposed VDA booking</h2>
          {pb ? (
            <dl className="space-y-2 text-sm">
              <Row l="Reference" v={pb.human_reference} />
              <Row l="Registration" v={pb.vehicles?.registration} />
              <Row l="Driver" v={pb.drivers?.full_name} />
              <Row l="Customer" v={pb.customers?.name} />
              <Row l="Current status" v={pb.public_status} />
              <Row l="Current appointment" v={pb.appointment_at ? fmtDateTime(pb.appointment_at) : "Not set"} />
              <Row l="Current garage" v={pb.garage_name || "Not set"} />
            </dl>
          ) : (
            <div>
              <p className="text-sm text-muted-foreground">No booking matched automatically. Select the correct Service/MOT request below.</p>
              {candidates.length > 0 && (
                <div className="mt-3 space-y-2" data-testid="conf-candidates">
                  {candidates.map((cd) => (
                    <div key={cd.id} className="flex items-center justify-between border border-border rounded-[6px] px-3 py-2">
                      <div className="text-sm">
                        <span className="vda-reg">{cd.vehicles?.registration}</span>
                        <span className="text-muted-foreground text-xs ml-2">{cd.human_reference} · {cd.customers?.name}</span>
                      </div>
                      <Btn variant="clay" data-testid={`choose-${cd.id}`} onClick={() => choose(cd.id)} disabled={busy}>Choose</Btn>
                    </div>
                  ))}
                </div>
              )}
              <ManualSearch onChoose={choose} busy={busy} />
            </div>
          )}
        </Panel>
      </div>

      {/* Mobile mismatch flag + SMS preview */}
      {(mobileMismatch || smsPreview) && (
        <div className="grid lg:grid-cols-2 gap-5 mt-5">
          {mobileMismatch ? (
            <Panel className="p-5 bg-[hsl(var(--vda-amber-bg))] border border-[hsl(var(--vda-amber))]/25" data-testid="conf-mobile-mismatch">
              <div className="flex items-start gap-2">
                <AlertTriangle className="h-4 w-4 mt-0.5 flex-none text-[hsl(var(--vda-amber))]" />
                <div className="text-sm">
                  <div className="font-medium text-[hsl(var(--vda-amber))]">Confirmation mobile differs from the active driver</div>
                  <p className="text-xs text-muted-foreground mt-1">
                    The mobile in this confirmation email does not match the verified mobile of the vehicle's active allocated driver. The SMS will always go to the <span className="font-medium">active driver</span> — never the email address in the confirmation.
                  </p>
                  <dl className="mt-3 space-y-1.5">
                    <div className="grid grid-cols-[150px_1fr] gap-3"><dt className="vda-tick text-muted-foreground">In confirmation</dt><dd className="font-mono text-xs">{mobileCheck.candidate_masked || mobileCheck.candidate || "—"}</dd></div>
                    <div className="grid grid-cols-[150px_1fr] gap-3"><dt className="vda-tick text-muted-foreground">Active driver (SMS to)</dt><dd className="font-mono text-xs">{mobileCheck.recipient_masked || "—"}</dd></div>
                  </dl>
                </div>
              </div>
            </Panel>
          ) : <div className="hidden lg:block" />}

          {smsPreview && smsPreview.available ? (
            <Panel className="p-5" data-testid="conf-sms-preview">
              <div className="flex items-center justify-between mb-3">
                <h2 className="font-semibold">SMS preview</h2>
                <Tag tone="muted">{smsPreview.delivery_mode === "simulated" ? "Simulated" : smsPreview.delivery_mode}</Tag>
              </div>
              <p className="text-xs text-muted-foreground mb-3">This is the confirmation text the active driver would receive on apply. Delivery is simulated in UAT.</p>
              <div className="rounded-[10px] bg-secondary/60 border border-border px-4 py-3 text-sm leading-relaxed font-mono" data-testid="conf-sms-body">
                {smsPreview.message_body}
              </div>
              <p className="text-[11px] text-[hsl(var(--vda-quiet))] font-mono mt-2">→ {smsPreview.recipient_masked || "active driver"}</p>
            </Panel>
          ) : smsPreview && !smsPreview.available ? (
            <Panel className="p-5" data-testid="conf-sms-preview-unavailable">
              <h2 className="font-semibold mb-2">SMS preview</h2>
              <p className="text-sm text-muted-foreground">No SMS will be sent — {smsPreview.reason === "no_active_driver_allocation" ? "this vehicle has no active driver allocation." : "preview unavailable."}</p>
            </Panel>
          ) : <div className="hidden lg:block" />}
        </div>
      )}

      {/* Actions */}
      {!isApplied && !isRejected && (
        <Panel className="p-5 mt-5" data-testid="conf-actions">
          <h2 className="font-semibold mb-3">Apply or reject</h2>
          <div className="flex flex-wrap items-end gap-4">
            <Btn data-testid="conf-apply" onClick={apply} disabled={busy || !pb}><Check className="h-4 w-4" /> Apply & confirm booking</Btn>
            <div className="flex items-end gap-2 flex-1 min-w-[260px]">
              <div className="flex-1"><Field label="Reason (if rejecting)"><Input data-testid="reject-reason" value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} placeholder="e.g. wrong vehicle / duplicate" /></Field></div>
              <Btn variant="danger" data-testid="conf-reject" onClick={reject} disabled={busy}><X className="h-4 w-4" /> Reject</Btn>
            </div>
          </div>
          {!pb && <p className="text-xs text-muted-foreground mt-3">Match a booking before you can apply.</p>}
          <p className="text-xs text-muted-foreground mt-2">Applying updates the booking's appointment, garage and public status, and records a driver-visible update.</p>
        </Panel>
      )}
      {isApplied && <Panel className="p-4 mt-5 text-sm text-primary" data-testid="conf-applied-note"><Check className="h-4 w-4 inline mr-1" /> Applied to {pb?.human_reference || "the booking"} — the driver now sees the confirmed appointment.</Panel>}
      {isRejected && <Panel className="p-4 mt-5 text-sm text-muted-foreground" data-testid="conf-rejected-note">Rejected{c.rejection_reason ? ` — ${c.rejection_reason}` : ""}.</Panel>}
    </StaffShell>
  );
}

function ManualSearch({ onChoose, busy }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);

  const search = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from("booking_requests")
        .select("id,human_reference,internal_status,customers(name),vehicles(registration)")
        .eq("request_category", "service_mot")
        .not("internal_status", "in", "(completed,cancelled)")
        .order("created_at", { ascending: false })
        .limit(200);
      if (error) throw error;
      const term = q.trim().toLowerCase();
      const norm = term.replace(/\s+/g, "");
      const filtered = (data || []).filter((b) => {
        const reg = (b.vehicles?.registration || "").toLowerCase().replace(/\s+/g, "");
        const ref = (b.human_reference || "").toLowerCase();
        return !term || reg.includes(norm) || ref.includes(term);
      }).slice(0, 20);
      setResults(filtered);
    } catch (e) { toast.error(e.message || "Search failed"); }
    finally { setLoading(false); }
  };

  return (
    <div className="mt-4 pt-4 border-t border-border" data-testid="conf-manual-search">
      <span className="vda-tick text-muted-foreground">Find the booking manually</span>
      <div className="flex gap-2 mt-2">
        <div className="relative flex-1">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input data-testid="manual-search-input" value={q} onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && search()} placeholder="Registration or VDA reference" className="pl-9" />
        </div>
        <Btn variant="outline" data-testid="manual-search-btn" onClick={search} loading={loading}>Search</Btn>
      </div>
      {results && (results.length === 0 ? (
        <p className="text-xs text-muted-foreground mt-3">No eligible Service/MOT requests found.</p>
      ) : (
        <div className="mt-3 space-y-2">
          {results.map((b) => (
            <div key={b.id} className="flex items-center justify-between border border-border rounded-[6px] px-3 py-2">
              <div className="text-sm">
                <span className="vda-reg">{b.vehicles?.registration}</span>
                <span className="text-muted-foreground text-xs ml-2">{b.human_reference} · {b.customers?.name}</span>
              </div>
              <Btn variant="clay" data-testid={`manual-choose-${b.id}`} onClick={() => onChoose(b.id)} disabled={busy}>Choose</Btn>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

function Row({ l, v }) {
  return (
    <div className="grid grid-cols-[140px_1fr] gap-3">
      <dt className="vda-tick text-muted-foreground">{l}</dt>
      <dd className={v ? "" : "text-muted-foreground/60"}>{v || "—"}</dd>
    </div>
  );
}
